
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ArrowLeft, ArrowUp, ArrowRight } from "lucide-react";
import { ExternalRoundStats } from "@/hooks/useCourseStats";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import { Progress } from "@/components/ui/progress";


interface BestPerformanceSectionProps {
  stats: ExternalRoundStats[];
}

const BestPerformanceSection = ({ stats }: BestPerformanceSectionProps) => {
  const { t, i18n } = useTranslation('course-stats');

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Find best performance
  const bestRound = stats.reduce((best, current) => {
    if (!best) return current;
    if ((current.score_vs_par || Infinity) < (best.score_vs_par || Infinity)) {
      return current;
    }
    return best;
  }, null as ExternalRoundStats | null);

  // Calculate fairway tendencies
  const totalTendencies = stats.reduce(
    (acc, s) => {
      if (s.fairway_tendencies) {
        acc.left += s.fairway_tendencies.left || 0;
        acc.center += s.fairway_tendencies.center || 0;
        acc.right += s.fairway_tendencies.right || 0;
      }
      return acc;
    },
    { left: 0, center: 0, right: 0 }
  );

  const totalFairwayShots = totalTendencies.left + totalTendencies.center + totalTendencies.right;

  // Course performance data
  const courseStats = stats.reduce((acc, s) => {
    const courseName = s.course_name || "Unknown";
    if (!acc[courseName]) {
      acc[courseName] = { count: 0, totalScore: 0 };
    }
    acc[courseName].count++;
    acc[courseName].totalScore += s.score_vs_par || 0;
    return acc;
  }, {} as Record<string, { count: number; totalScore: number }>);

  const coursePerformanceData = Object.entries(courseStats)
    .map(([name, data]) => ({
      name,
      fullName: name,
      value: data.count,
      avgScore: data.totalScore / data.count,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const totalRounds = coursePerformanceData.reduce((sum, c) => sum + c.value, 0);
  const maxRounds = Math.max(...coursePerformanceData.map(c => c.value));

  const COLORS = ['hsl(var(--primary))', 'hsl(160 60% 45%)', 'hsl(200 80% 50%)', 'hsl(30 80% 55%)', 'hsl(270 60% 55%)'];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Best Performance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5 text-yellow-500" />
            {t('bestPerformance.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bestRound ? (
            <div className="space-y-3">
              <div className="text-3xl font-bold text-golf-800">
                {(bestRound.score_vs_par || 0) >= 0 ? "+" : ""}
                {bestRound.score_vs_par}
              </div>
              <div className="text-sm text-muted-foreground">
                {bestRound.course_name}
              </div>
              <div className="text-sm text-muted-foreground">
                {format(new Date(bestRound.round_date), "PPP", { locale: getLocale() })}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <div className="text-center">
                  <div className="text-lg font-semibold">{bestRound.total_score}</div>
                  <div className="text-xs text-muted-foreground">{t('bestPerformance.totalScore')}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold">{bestRound.total_putts}</div>
                  <div className="text-xs text-muted-foreground">{t('bestPerformance.putts')}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground">{t('bestPerformance.noData')}</div>
          )}
        </CardContent>
      </Card>

      {/* Fairway Tendencies */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('tendencies.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <ArrowLeft className="h-5 w-5 text-red-500" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('tendencies.left')}</span>
                  <span>{totalFairwayShots > 0 ? ((totalTendencies.left / totalFairwayShots) * 100).toFixed(0) : '0'}%</span>
                </div>
                <Progress value={totalFairwayShots > 0 ? (totalTendencies.left / totalFairwayShots) * 100 : 0} className="h-2" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ArrowUp className="h-5 w-5 text-green-500" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('tendencies.center')}</span>
                  <span>{totalFairwayShots > 0 ? ((totalTendencies.center / totalFairwayShots) * 100).toFixed(0) : '0'}%</span>
                </div>
                <Progress value={totalFairwayShots > 0 ? (totalTendencies.center / totalFairwayShots) * 100 : 0} className="h-2" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ArrowRight className="h-5 w-5 text-orange-500" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('tendencies.right')}</span>
                  <span>{totalFairwayShots > 0 ? ((totalTendencies.right / totalFairwayShots) * 100).toFixed(0) : '0'}%</span>
                </div>
                <Progress value={totalFairwayShots > 0 ? (totalTendencies.right / totalFairwayShots) * 100 : 0} className="h-2" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Course Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('courseDistribution.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {coursePerformanceData.length > 0 ? (
            <div className="space-y-3">
              {coursePerformanceData.map((course, index) => (
                <div key={course.name} className="space-y-1">
                  <div className="flex items-center justify-between text-sm gap-2">
                    <span 
                      className="font-medium truncate flex-1 min-w-0" 
                      title={course.fullName}
                    >
                      {course.fullName}
                    </span>
                    <div className="flex items-center gap-3 text-muted-foreground whitespace-nowrap">
                      <span>
                        {course.value} {course.value > 1 ? t('courseDistribution.rounds') : t('courseDistribution.round')}
                      </span>
                      <span className="font-medium text-foreground">
                        {course.avgScore >= 0 ? '+' : ''}{course.avgScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(course.value / maxRounds) * 100}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted-foreground">{t('courseDistribution.noData')}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BestPerformanceSection;
